import { useCallback, useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Error, useErrorReset } from '@/components/ui/Error'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { TextField } from '@/components/ui/text-field'
import { useToast } from '@/components/ui/toast.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import type { IconType } from '@/modules/tasks/components/icons'
import { icons, useIcon } from '@/modules/tasks/components/icons'
import {
  useCreateList,
  useUpdateList,
} from '@/modules/tasks/query/task-list-query'
import type { ListForm } from '@/modules/tasks/service/task-list-service'
import { EditListSchema } from '@/modules/tasks/service/task-list-service'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'

interface CreateListProps {
  isOpen: boolean
  onClose: () => void
  onCreated?: (list: ListForm) => void
  initial?: ListForm
}

const defaultValues: ListForm = {
  color: '#3537f6',
  icon: 'Home',
  detail: '',
  tasks: [],
  name: '',
}

const resolver = zodResolver(
  EditListSchema.extend({ id: z.number().optional().nullish() }),
)

export const CreateListDialog = ({
  isOpen,
  onClose,
  initial,
}: CreateListProps) => {
  const toast = useToast()
  const create = useCreateList()
  const update = useUpdateList()
  const [error, resetError] = useErrorReset(create.error)

  const form = useForm<ListForm & { id?: number | undefined }>({
    defaultValues,
    resolver,
  })

  const color = form.watch('color')

  const isEdit = initial != null

  useEffect(() => resetError(), [isOpen, resetError])

  useEffect(() => {
    return () => form.reset(defaultValues)
  }, [form, isOpen])

  useEffect(() => {
    form.reset(initial ? { ...defaultValues, ...initial } : defaultValues)
  }, [form, initial])

  const onSubmit = useCallback(
    (state: ListForm & { id?: number | null }) => {
      const form = { ...state, tasks: [], repeatableTasks: [] }

      if (state.id != null) {
        update
          .mutateAsync({ id: state.id, form })
          .then(() => {
            toast.success('List updated')
            onClose()
          })
          .catch((error) => {
            handleError(error, 'Failed to update list')
          })

        return
      }
      create
        .mutateAsync(form)
        .then(() => {
          toast.success('List created')
          onClose()
        })
        .catch((error) => {
          handleError(error, 'Failed to create list')
        })
    },
    [create, update, toast, onClose],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title className="text-center">
          {initial?.name || 'New List'}
        </Dialog.Title>
      </Dialog.Header>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <FormField
              id="list-name"
              name="name"
              label="List Name"
              placeholder="Enter list name"
              className="w-full"
            />
            <div className="grid gap-4 sm:grid-flow-col sm:grid-cols-2">
              <div className="grid sm:col-span-1">
                <Controller
                  name="color"
                  render={({ field, fieldState: { error } }) => (
                    <div className="flex w-full flex-col gap-1">
                      <label
                        htmlFor={field.name}
                        className="text-sm font-medium text-title"
                      >
                        Color
                      </label>
                      <TextField
                        {...field}
                        className="text-title"
                        placeholder="#000000"
                        type="text"
                        prefix={
                          <div
                            className="h-6 w-6 flex-shrink-0 cursor-pointer rounded-full"
                            style={{ backgroundColor: field.value }}
                            onClick={() => {
                              openDialog({
                                type: DIALOG_TYPE.COLOR_PICKER,
                                onColorChange: field.onChange,
                                oldColor: field.value,
                              })
                            }}
                          />
                        }
                      />
                      {error && (
                        <p className="text-xs text-danger-default">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="col-span-1 grid">
                <Controller
                  name="icon"
                  render={({ field }) => (
                    <IconSelector
                      color={color}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <Error
            error={error}
            className="mt-2"
          />
          <Dialog.ClosableFooter onClose={onClose}>
            <Button
              type="submit"
              isLoading={create.isLoading || update.isLoading}
            >
              {isEdit ? 'Save Changes' : 'Create List'}
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}

interface IconSelectorProps {
  color?: string | null
  value: IconType
  onChange?: (value: IconType) => void
}

const IconSelector = ({ color, value, onChange }: IconSelectorProps) => {
  const iconPreview = useIcon(value, color)

  const options = useMemo(() => {
    return Object.entries(icons).map(([key, Icon]) => (
      <button
        type="button"
        key={key}
        onClick={() => onChange?.(key as IconType)}
        className={classNames(
          'col-span-1 flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-md transition-all duration-300 ease-out',
          {
            'bg-primary-500 text-neutral-default': key === value,
            'text-title hover:bg-primary-500/20 hover:text-primary-500 ':
              key !== value,
          },
        )}
      >
        <Icon
          height={16}
          width={16}
        />
        <span className="hidden text-xs">{key}</span>
      </button>
    ))
  }, [onChange, value])

  return (
    <div className="col-span-6 flex w-full flex-col gap-1">
      <label
        htmlFor="icon"
        className="text-sm font-medium text-title"
      >
        Icon
      </label>
      <Popover>
        <PopoverTrigger>
          <div className="group relative col-span-12 flex w-full items-center gap-2 rounded-md border bg-card px-3 py-2 font-medium focus-within:border-primary-500 dark:hover:bg-secondary-600 sm:col-span-6">
            {iconPreview}
            <div className="flex-1 text-left text-title">{value}</div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[102] max-w-[300px] select-auto rounded-lg border-0 p-6 shadow-2xl"
        >
          <div className="grid grid-cols-7 gap-1">{options}</div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
